import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useCookieConsent } from '../context/CookieConsentContext';

/**
 * Cookie Banner LPD/GDPR compliant.
 * - Bottom sticky, non blocca l'interazione
 * - 2 CTA equivalentemente accessibili (no dark pattern)
 * - "Accetta" leggermente più prominente (ammesso legalmente: UI hierarchy ok)
 * - Nessuna opzione di chiusura senza scelta → riappare finché utente sceglie
 */
export function CookieBanner() {
  const { t } = useTranslation();
  const { bannerVisible, acceptAll, rejectAll } = useCookieConsent();

  if (!bannerVisible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed bottom-0 inset-x-0 z-[60] pointer-events-none px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="pointer-events-auto max-w-3xl mx-auto rounded-2xl border border-border/70 bg-card/95 backdrop-blur-xl shadow-2xl p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Cookie size={18} className="text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="cookie-banner-title" className="text-sm font-bold flex items-center gap-1.5">
                {t('cookieTitle')}
              </h3>
              <p id="cookie-banner-desc" className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {t('cookieDesc')}{' '}
                <a
                  href="/privacy"
                  className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                  data-testid="cookie-banner-privacy-link"
                >
                  {t('cookiePrivacyLink')}
                </a>
              </p>
              <p className="text-[10px] text-muted-foreground/70 mt-1 flex items-center gap-1">
                <Shield size={9} /> {t('cookieLegalNote')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:flex-col sm:items-stretch flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              data-testid="cookie-reject-all"
              onClick={rejectAll}
              className="text-xs h-8 px-3 whitespace-nowrap"
            >
              {t('cookieRejectBtn')}
            </Button>
            <Button
              size="sm"
              data-testid="cookie-accept-all"
              onClick={acceptAll}
              className="text-xs h-8 px-4 bg-amber-500 hover:bg-amber-600 text-white font-medium whitespace-nowrap shadow-sm"
            >
              {t('cookieAcceptBtn')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Link footer "Gestisci cookie" — riapre il banner.
 */
export function CookieSettingsLink({ className = '' }) {
  const { t } = useTranslation();
  const { openBanner, status, expiresAt } = useCookieConsent();
  return (
    <button
      onClick={openBanner}
      data-testid="cookie-settings-link"
      className={`hover:text-foreground transition-colors inline-flex items-center gap-1 ${className}`}
      title={status ? `${t('cookieCurrentStatus')}: ${status} (${expiresAt ? new Date(expiresAt).toLocaleDateString() : ''})` : ''}
    >
      <Cookie size={11} /> {t('cookieSettingsLink')}
    </button>
  );
}
